import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useTheme } from '@/hooks/useTheme';
import { useUnreadMessagesCount } from '@/hooks/useUnreadMessages';
import { useJobsHomePath, useJobsViews } from '@/components/jobs/jobsViews';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  UserPlus,
  Briefcase,
  FileText,
  FileCheck,
  BarChart3,
  Settings,
  Users,
  Activity,
  Shield,
  MessageCircle,
  FolderKanban,
  Sun,
  Moon,
  LogOut,
  User,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { APP_CONFIG } from '@/config/app';

interface RailItemProps {
  label: string;
  children: React.ReactNode;
}

/** 44x44 rail cell wrapped in a right-side tooltip (the rail has no labels). */
function RailTooltip({ label, children }: RailItemProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  /** Tooltip suffix explaining the badge. */
  badgeLabel?: string;
  /** Lit for every path under this prefix (a section with several views). */
  section?: string;
}

const railItemClass = (isActive: boolean) =>
  cn(
    'relative flex h-11 w-11 items-center justify-center rounded-[10px] transition-colors',
    isActive
      ? 'bg-sidebar-primary text-sidebar-primary-foreground'
      : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground',
  );

export function AppSidebar() {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUserRole();
  const { isSuperAdmin } = useSuperAdmin();
  const { enableExtendedProjects } = useCompanySettings();
  const { theme, toggleTheme } = useTheme();
  const { data: unreadCount = 0 } = useUnreadMessagesCount();
  const { views: jobsViews, toBookCount } = useJobsViews();
  const jobsHome = useJobsHomePath(jobsViews);

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/communications', label: 'Communications', icon: MessageCircle, badge: unreadCount },
    { path: '/leads', label: 'Leads', icon: UserPlus },
    {
      path: jobsHome,
      section: '/jobs',
      label: 'Jobs',
      icon: Briefcase,
      badge: toBookCount,
      badgeLabel: toBookCount ? `${toBookCount} to book` : undefined,
    },
    ...(enableExtendedProjects
      ? [{ path: '/projects', label: 'Projects', icon: FolderKanban }]
      : []),
    { path: '/estimates', label: 'Estimates', icon: FileCheck },
    { path: '/invoices', label: 'Invoices', icon: FileText },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase();

  return (
    <TooltipProvider delayDuration={0}>
      <aside className="flex h-full w-[72px] shrink-0 flex-col items-center bg-sidebar py-4">
        {/* Logo mark */}
        <img src="/logo/logo-mark-white.svg" alt={APP_CONFIG.name} className="mb-4 h-9 w-9" />

        {/* Primary navigation */}
        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <RailTooltip key={item.section ?? item.path} label={item.badgeLabel ? `${item.label} · ${item.badgeLabel}` : item.label}>
                {/* Plain string className: TooltipTrigger's Slot string-joins className,
                    which stringifies NavLink's function form into a garbage class. */}
                <NavLink
                  to={item.path}
                  end
                  className={railItemClass(
                    item.section
                      ? location.pathname === item.section || location.pathname.startsWith(`${item.section}/`)
                      : location.pathname === item.path,
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                  {item.badge ? (
                    <span className="absolute -right-0.5 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border-2 border-sidebar bg-primary px-0.5 text-[9px] font-bold leading-none text-primary-foreground">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  ) : null}
                </NavLink>
              </RailTooltip>
            );
          })}
        </nav>

        {/* Bottom cluster: system health, theme toggle, account */}
        <div className="flex flex-col items-center gap-1 pt-2">
          {isSuperAdmin && (
            <RailTooltip label="Multi-Tenant">
              <NavLink
                to="/settings/super-admin"
                end
                className={railItemClass(location.pathname === '/settings/super-admin')}
              >
                <Shield className="h-[18px] w-[18px]" />
              </NavLink>
            </RailTooltip>
          )}

          {isAdmin && (
            <RailTooltip label="System Health">
              <NavLink
                to="/system-health"
                end
                className={railItemClass(location.pathname === '/system-health')}
              >
                <Activity className="h-[18px] w-[18px]" />
                <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-success" />
              </NavLink>
            </RailTooltip>
          )}

          <RailTooltip label={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            <button type="button" onClick={toggleTheme} className={railItemClass(false)}>
              {theme === 'dark' ? (
                <Sun className="h-[18px] w-[18px]" />
              ) : (
                <Moon className="h-[18px] w-[18px]" />
              )}
            </button>
          </RailTooltip>

          <DropdownMenu>
            <RailTooltip label={user?.email ?? 'Account'}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="mt-1 flex h-[34px] w-[34px] items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground"
                  aria-label="Account menu"
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
            </RailTooltip>
            <DropdownMenuContent side="right" align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={signOut}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </TooltipProvider>
  );
}
